import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { FaSearch, FaCar, FaMotorcycle, FaChevronDown, FaChevronUp, FaPhone } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import CommunityIdentity from '../common/CommunityIdentity';
import Table from '../common/Table';
import useApi from '../../hooks/useApi';
import 'animate.css';

/**
 * Component to search and manage guest vehicles
 * @param {Object} props
 * @param {boolean} props.isSuperAdmin - Indicates if user is SUPER_ADMIN
 */
function SearchVehicle({ isSuperAdmin }) {
  const { session } = useContext(AuthContext);
  const { communityId } = session || {};
  const [searchType, setSearchType] = useState('vehicleNo');
  const [formData, setFormData] = useState({ vehicleNo: '', houseNo: '', division: '' });
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const { get } = useApi();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setError('');
    if (searchType === 'vehicleNo' && !formData.vehicleNo.trim()) {
      setError('Please enter a vehicle number.');
      return;
    }
    if (searchType === 'house') {
      if (!formData.houseNo.trim()) {
        setError('Please enter a house number.');
        return;
      }
      if (!formData.division.trim()) {
        setError('Please select a division.');
        return;
      }
    }
    try {
      let response;
      const params = {
        ...(searchType === 'vehicleNo' ? { vehicleNo: formData.vehicleNo } : { houseNo: formData.houseNo, division: formData.division }),
        ...(!isSuperAdmin && communityId ? { communityId } : {}),
      };
      if (searchType === 'vehicleNo') {
        response = await get('/api/vehicles/search/vehicleNo', { params });
      } else {
        response = await get('/api/vehicles/search/house', { params });
      }
      if (!response || response.length === 0) {
        setError('No vehicles match your search criteria.');
        setResults([]);
      } else {
        setResults(response);
        setError('');
      }
    } catch (err) {
      console.error('SearchVehicle - Search error:', err);
      setError('No vehicles match your search criteria.');
      setResults([]);
    }
  };

  const handleClear = () => {
    setFormData({ vehicleNo: '', houseNo: '', division: '' });
    setSearchType('vehicleNo');
    setResults([]);
    setError('');
    setIsSubmitted(false);
    setExpandedVehicle(null);
  };

  // Format phone number (add +91, validate exactly 10 digits)
  const formatPhoneNumber = (number) => {
    if (!number || typeof number !== 'string') {
      toast.error('Invalid phone number: No number provided');
      return '';
    }
    const trimmedNumber = number.trim();
    const cleanNumber = trimmedNumber.replace(/\D/g, '');
    if (cleanNumber.length !== 10) {
      toast.error('Invalid phone number: Must be exactly 10 digits');
      return '';
    }
    return `${cleanNumber}`; // You can prepend '+91' if needed: `+91${cleanNumber}`
  };

  const toggleExpand = (vehicleNo) => {
    setExpandedVehicle(expandedVehicle === vehicleNo ? null : vehicleNo);
  };

  const columns = isSuperAdmin
    ? ['Vehicle No', 'Type', 'Owner Name', 'House No', 'Division', 'Community', 'Mobile No']
    : ['Vehicle No', 'Type', 'Owner Name', 'House No', 'Division', 'Mobile No'];

  const renderTableRow = (result) => {
    const phoneNumber = result.mobileNo && result.mobileNo.replace(/\D/g, '').length === 10
      ? formatPhoneNumber(result.mobileNo)
      : result.mobileNo || 'Not available';
    const isValidPhone = phoneNumber && phoneNumber.length === 10;
    return (
      <tr key={result.vehicleNo}>
        <td>{result.vehicleNo}</td>
        <td>
          {result.type === 'two-wheeler' ? (
            <FaMotorcycle size={24} color="#007bff" title="Two-Wheeler" />
          ) : (
            <FaCar size={24} color="#28a745" title="Four-Wheeler" />
          )}
        </td>
        <td>{result.userName}</td>
        <td>{result.houseNo}</td>
        <td>{result.division}</td>
        {isSuperAdmin && (
          <td>{result.community ? <CommunityIdentity community={result.community} size="xs" compact /> : 'N/A'}</td>
        )}
        <td>
          {phoneNumber}
          {isValidPhone && (
            <a href={`tel:${phoneNumber}`} className="text-success ms-1">
              <FaPhone size={14} />
            </a>
          )}
        </td>
      </tr>
    );
  };

  const renderCard = (result) => {
    const isExpanded = expandedVehicle === result.vehicleNo;
    const phoneNumber = result.mobileNo && result.mobileNo.replace(/\D/g, '').length === 10
      ? formatPhoneNumber(result.mobileNo)
      : result.mobileNo || 'Not available';
    const isValidPhone = phoneNumber && phoneNumber.length === 10;
    return (
      <div
        key={result.vehicleNo}
        className="card shadow-sm mb-3 animate__animated animate__fadeInUp"
      >
        <div
          className="card-header d-flex justify-content-between align-items-center bg-light"
          onClick={() => toggleExpand(result.vehicleNo)}
          style={{ cursor: 'pointer' }}
        >
          <h6 className="mb-0">{result.vehicleNo}</h6>
          <span className="d-flex align-items-center">
            {result.type === 'two-wheeler' ? (
              <FaMotorcycle size={24} color="#007bff" title="Two-Wheeler" />
            ) : (
              <FaCar size={24} color="#28a745" title="Four-Wheeler" />
            )}
          </span>
        </div>
        <div className="card-body">
          {isExpanded && (
            <>
              <p className="mb-1">
                <strong>Owner:</strong> {result.userName || '-'}
              </p>
              <p className="mb-1">
                <strong>House No:</strong> {result.houseNo || '-'}
              </p>
              <p className="mb-1">
                <strong>Division:</strong> {result.division || '-'}
              </p>
              {isSuperAdmin && (
                <div className="mb-1">
                  <strong>Community:</strong>{' '}
                  {result.community ? <CommunityIdentity community={result.community} size="xs" compact /> : 'N/A'}
                </div>
              )}
              <p className="mb-1">
                <strong>Mobile No:</strong>{' '}
                {phoneNumber}
                {isValidPhone && (
                  <a href={`tel:${phoneNumber}`} className="text-success ms-1">
                    <FaPhone size={14} />
                  </a>
                )}
              </p>
            </>
          )}
        </div>
        <div
          className="card-footer text-center bg-light"
          onClick={() => toggleExpand(result.vehicleNo)}
          style={{ cursor: 'pointer' }}
        >
          {isExpanded ? (
            <FaChevronUp size={16} className="text-muted" />
          ) : (
            <FaChevronDown size={16} className="text-muted" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <h2 className="mb-4 fs-4 fw-bold">Search Vehicle</h2>
      <div className="card shadow mb-4">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3">Search Vehicle</h5>
          {isSubmitted && error && <Alert type="danger" message={error} />}
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <div className="mb-3">
                  <label className="form-label">Search By</label>
                  <select
                    className="form-select"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <option value="vehicleNo">Vehicle Number</option>
                    <option value="house">House No & Division</option>
                  </select>
                </div>
              </div>
              {searchType === 'vehicleNo' ? (
                <div className="col-12 col-md-4">
                  <FormInput
                    label="Vehicle Number"
                    name="vehicleNo"
                    value={formData.vehicleNo}
                    onChange={handleChange}
                    required
                  />
                </div>
              ) : (
                <>
                  <div className="col-12 col-md-4">
                    <FormInput
                      label="House Number"
                      name="houseNo"
                      value={formData.houseNo}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="mb-3">
                      <label htmlFor="division" className="form-label">Division</label>
                      <select
                        className="form-select"
                        id="division"
                        name="division"
                        value={formData.division}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Division</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
              <div className="col-12 col-md-4 d-flex align-items-end">
                <div className="d-flex gap-2 w-100">
                  <Button type="submit" variant="primary" className="w-50">
                    <FaSearch /> Search
                  </Button>
                  <Button type="button" variant="secondary" className="w-50" onClick={handleClear}>
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      {isSubmitted && results.length > 0 && (
        <>
          {/* Desktop Table */}
          <div className="card shadow mx-auto d-none d-md-block" style={{ maxWidth: '1200px' }}>
            <div className="card-body">
              <h5 className="card-title fs-5 mb-3">Vehicles</h5>
              <Table columns={columns} renderRow={renderTableRow} data={results} />
            </div>
          </div>
          {/* Mobile Cards */}
          <div className="d-md-none">{results.map((result) => renderCard(result))}</div>
        </>
      )}
      <style>
        {`
          /* Mobile (≤576px) */
          @media (max-width: 576px) {
            .card {
              font-size: 0.75rem; /* 12px */
              border-radius: 0.5rem;
              margin-bottom: 0.75rem;
            }
            .card-header,
            .card-footer {
              font-size: 0.875rem; /* 14px */
              padding: 0.5rem;
            }
            .card-body {
              padding: 0.75rem !important;
            }
            .card .btn {
              font-size: 0.75rem; /* 12px */
              padding: 0.25rem 0.5rem;
            }
            .form-select, .form-control {
              font-size: 0.875rem; /* 14px */
            }
          }

          /* Tablet (576-768px) */
          @media (min-width: 576px) and (max-width: 768px) {
            .table th, .table td {
              font-size: 0.875rem; /* 14px */
              padding: 0.5rem;
            }
            .table .btn {
              font-size: 0.875rem; /* 14px */
            }
          }

          /* Desktop (≥768px) */
          @media (min-width: 768px) {
            .table th, .table td {
              font-size: 1rem; /* 16px */
              padding: 0.75rem;
            }
            .table .btn {
              font-size: 1rem; /* 16px */
            }
            .table tr:hover {
              background-color: #f8f9fa;
            }
          }
        `}
      </style>
    </div>
  );
}

SearchVehicle.propTypes = {
  isSuperAdmin: PropTypes.bool,
};

export default SearchVehicle;
